// Three.js Rendering Engine for CoandaView

/**
 * Parse binary geometry format (GEO1)
 * Much faster than JSON for large geometries
 *
 * Binary Format:
 * Header (40 bytes): magic, version, num_vertices, num_triangles, num_scalars,
 *                    scalar_min, scalar_max, flags, num_vectors, metadata_size
 * Data: vertices, triangles, scalars, vectors (if flags & 1), metadata JSON
 */
function parseBinaryGeometry(arrayBuffer) {
  const view = new DataView(arrayBuffer);
  let offset = 0;

  // Read header
  const magic = String.fromCharCode(
    view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3)
  );
  offset += 4;

  if (magic !== 'GEO1') {
    throw new Error(`Invalid binary geometry magic: ${magic}`);
  }

  const version = view.getUint32(offset, true); offset += 4;
  const numVertices = view.getUint32(offset, true); offset += 4;
  const numTriangles = view.getUint32(offset, true); offset += 4;
  const numScalars = view.getUint32(offset, true); offset += 4;
  const scalarMin = view.getFloat32(offset, true); offset += 4;
  const scalarMax = view.getFloat32(offset, true); offset += 4;
  const flags = view.getUint32(offset, true); offset += 4;
  const numVectors = view.getUint32(offset, true); offset += 4;
  const metadataSize = view.getUint32(offset, true); offset += 4;

  const hasVectors = (flags & 1) !== 0;

  // Read vertices (numVertices * 3 floats)
  const verticesBytes = numVertices * 3 * 4;
  const vertices = new Float32Array(arrayBuffer, offset, numVertices * 3);
  offset += verticesBytes;

  // Read triangles (numTriangles * 3 uint32)
  const trianglesBytes = numTriangles * 3 * 4;
  const triangles = new Uint32Array(arrayBuffer, offset, numTriangles * 3);
  offset += trianglesBytes;

  // Read scalars (numScalars floats)
  const scalarsBytes = numScalars * 4;
  const scalars = numScalars > 0
    ? new Float32Array(arrayBuffer, offset, numScalars)
    : null;
  offset += scalarsBytes;

  // Read vectors if present (numVectors * 6 floats: px,py,pz,vx,vy,vz)
  let vectors = null;
  let vectorPositions = null;
  if (hasVectors && numVectors > 0) {
    const vectorsBytes = numVectors * 6 * 4;
    const vectorData = new Float32Array(arrayBuffer, offset, numVectors * 6);
    offset += vectorsBytes;

    // Split into positions and directions
    vectorPositions = [];
    vectors = [];
    for (let i = 0; i < numVectors; i++) {
      const base = i * 6;
      vectorPositions.push([vectorData[base], vectorData[base + 1], vectorData[base + 2]]);
      vectors.push([vectorData[base + 3], vectorData[base + 4], vectorData[base + 5]]);
    }
  }

  // Read metadata JSON
  const metadataBytes = new Uint8Array(arrayBuffer, offset, metadataSize);
  const metadataJson = new TextDecoder().decode(metadataBytes);
  const metadata = JSON.parse(metadataJson);

  // Check if this is split_by_parts geometry that needs reconstruction
  if (metadata.split_by_parts && metadata.part_boundaries && metadata.part_boundaries.length > 0) {
    console.log(`[BinaryGeometry] Reconstructing ${metadata.part_boundaries.length} parts from boundaries`);

    // Reconstruct parts array from unified data using boundaries
    const parts = [];

    for (const boundary of metadata.part_boundaries) {
      // Extract vertices for this part
      const partVertices = [];
      for (let i = 0; i < boundary.vertex_count; i++) {
        const idx = (boundary.vertex_start + i) * 3;
        partVertices.push([vertices[idx], vertices[idx + 1], vertices[idx + 2]]);
      }

      // Extract triangles for this part - need to remap indices to be relative to part vertices
      const partTriangles = [];
      for (let i = 0; i < boundary.triangle_count; i++) {
        const idx = (boundary.triangle_start + i) * 3;
        // Triangles reference global vertex indices, so we need to make them relative to part
        const t0 = triangles[idx] - boundary.vertex_start;
        const t1 = triangles[idx + 1] - boundary.vertex_start;
        const t2 = triangles[idx + 2] - boundary.vertex_start;
        partTriangles.push([t0, t1, t2]);
      }

      // Extract scalars for this part
      const partScalars = scalars
        ? Array.from(scalars.slice(boundary.scalar_start, boundary.scalar_start + boundary.scalar_count))
        : null;

      // Parse hierarchy from part name (e.g., "boundary/walls" -> groupPath ["boundary"])
      const fullName = boundary.name;
      const pathSegments = fullName.split('/');
      const partName = pathSegments[pathSegments.length - 1];
      const groupPath = pathSegments.slice(0, -1);

      parts.push({
        part_name: partName,
        full_name: fullName,
        group_path: groupPath,
        vertices: partVertices,
        triangles: partTriangles,
        scalars: partScalars,
        num_vertices: boundary.vertex_count,
        num_triangles: boundary.triangle_count,
        scalar_range: metadata.global_scalar_range
      });

      console.log(`[BinaryGeometry] Reconstructed part '${partName}': ${boundary.vertex_count} vertices, ${boundary.triangle_count} triangles`);
    }

    // Build result with split_by_parts structure
    const result = {
      status: 'success',
      split_by_parts: true,
      parts: parts,
      num_vertices: numVertices,
      num_triangles: numTriangles,
      scalar_field: metadata.scalar_field,
      representation: metadata.representation,
      boundaries: metadata.boundaries || {},
      available_vector_fields: metadata.available_vector_fields || [],
      available_parts: metadata.available_parts || [],
      selected_parts: metadata.selected_parts || [],
      global_scalar_range: metadata.global_scalar_range,
      field_metadata: metadata.field_metadata || {}
    };

    if (scalars) {
      result.scalar_range = { min: scalarMin, max: scalarMax };
    }

    if (vectors) {
      result.vectors = vectors;
      result.vector_positions = vectorPositions;
      result.vector_field = metadata.vector_field;
      result.vector_range = metadata.vector_range;
    }

    console.log(`[BinaryGeometry] Parsed split geometry: ${numVertices} vertices, ${numTriangles} triangles, ${parts.length} parts, boundaries:`, Object.keys(metadata.boundaries || {}));

    return result;
  }

  // Non-split geometry: reshape vertices and triangles for compatibility with existing code
  // Convert flat arrays to 2D-like structure expected by rendering
  const verticesArray = [];
  for (let i = 0; i < numVertices; i++) {
    verticesArray.push([vertices[i * 3], vertices[i * 3 + 1], vertices[i * 3 + 2]]);
  }

  const trianglesArray = [];
  for (let i = 0; i < numTriangles; i++) {
    trianglesArray.push([triangles[i * 3], triangles[i * 3 + 1], triangles[i * 3 + 2]]);
  }

  // Build result object matching JSON response format
  const result = {
    status: 'success',
    vertices: verticesArray,
    triangles: trianglesArray,
    num_vertices: numVertices,
    num_triangles: numTriangles,
    scalar_field: metadata.scalar_field,
    representation: metadata.representation,
    boundaries: metadata.boundaries || {},
    available_vector_fields: metadata.available_vector_fields || [],
    available_parts: metadata.available_parts || [],
    selected_parts: metadata.selected_parts || [],
    field_metadata: metadata.field_metadata || {}
  };

  if (scalars) {
    result.scalars = Array.from(scalars);
    result.scalar_range = { min: scalarMin, max: scalarMax };
  }

  if (vectors) {
    result.vectors = vectors;
    result.vector_positions = vectorPositions;
    result.vector_field = metadata.vector_field;
    result.vector_range = metadata.vector_range;
  }

  console.log(`[BinaryGeometry] Parsed: ${numVertices} vertices, ${numTriangles} triangles, ${numScalars} scalars, fields: ${Object.keys(metadata.field_metadata || {}).length}`);

  return result;
}

class RenderingEngine {
  constructor(container) {
    this.container = container;
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.controls = null;
    this.objects = new Map(); // Store rendered objects
    this.helpers = {
      axes: null,
      grid: null
    };

    this.stats = {
      points: 0,
      cells: 0,
      memory: 0,
      renderTime: 0,
      fps: 0
    };

    this.animationCallbacks = new Set();
    this.lastFrameTime = performance.now();

    this.settings = {
      backgroundColor: 0x1a1a1a,
      showAxes: true,
      showGrid: true,
      wireframe: false
    };

    this.initialize();
  }

  initialize() {
    // Create scene
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(this.settings.backgroundColor);

    // Create camera
    const aspect = this.container.clientWidth / this.container.clientHeight;
    this.camera = new THREE.PerspectiveCamera(60, aspect, 0.1, 10000);
    this.camera.position.set(5, 5, 5);
    this.camera.lookAt(0, 0, 0);

    // Create renderer
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      preserveDrawingBuffer: true // For screenshots
    });
    this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.shadowMap.enabled = false;
    this.container.appendChild(this.renderer.domElement);

    // Create trackball controls for free rotation in any direction
    this.controls = new THREE.TrackballControls(this.camera, this.renderer.domElement);
    this.controls.rotateSpeed = 1.5;
    this.controls.zoomSpeed = 1.0;
    this.controls.panSpeed = 0.6;
    this.controls.noZoom = false;
    this.controls.noPan = false;
    this.controls.staticMoving = false;
    this.controls.dynamicDampingFactor = 0.12;

    // Dynamically adjust clipping planes during zoom/pan
    this.controls.addEventListener('change', () => this.updateClippingPlanes());

    // Add lighting
    this.setupLighting();

    // Add helpers
    this.setupHelpers();

    // Handle window resize
    window.addEventListener('resize', () => this.handleResize());

    // Start animation loop
    this.animate();
  }

  setupLighting() {
    // Base intensities — tuned for balanced default appearance
    this._baseLightIntensities = { ambient: 0.45, directional: 0.65, hemisphere: 0.3 };
    this._lightLevel = 1.0;    // overall brightness multiplier (0–2)
    this._shadowLevel = 0.5;   // shadow contrast factor (0–1)

    // Ambient base — ensures no area is completely dark
    this.ambientLight = new THREE.AmbientLight(0xffffff, this._baseLightIntensities.ambient);
    this.scene.add(this.ambientLight);

    // Camera-following directional light — reveals surface form from every angle.
    // Attached to the camera so it always shines from the viewer's direction.
    this.cameraLight = new THREE.DirectionalLight(0xffffff, this._baseLightIntensities.directional);
    this.cameraLight.position.set(0.5, 0.8, 1); // slightly above and right of the view axis
    this.camera.add(this.cameraLight);
    this.scene.add(this.camera); // camera must be in scene graph for child lights

    // Hemisphere light for top/bottom depth cue
    this.hemisphereLight = new THREE.HemisphereLight(0xffffff, 0x888888, this._baseLightIntensities.hemisphere);
    this.scene.add(this.hemisphereLight);

    // Apply default shadow level to compute initial intensities
    this._applyLighting();
  }

  /**
   * Set overall light level (brightness multiplier).
   * @param {number} factor — 0 to 2 (1.0 = 100%)
   */
  setLightLevel(factor) {
    this._lightLevel = Math.max(0, Math.min(2, factor));
    this._applyLighting();
  }

  /**
   * Set shadow level (contrast between lit and shadowed areas).
   * @param {number} factor — 0 to 2 (0 = flat/no shadows, 1 = deep shadows, 2 = extreme contrast)
   */
  setShadowLevel(factor) {
    this._shadowLevel = Math.max(0, Math.min(2, factor));
    this._applyLighting();
  }

  /** Recompute light intensities from current lightLevel and shadowLevel. */
  _applyLighting() {
    const base = this._baseLightIntensities;
    const s = this._shadowLevel;
    const L = this._lightLevel;

    // Shadow level redistributes energy between ambient and directional:
    //  - s=0:   ambient at full, directional at half    → flat lighting
    //  - s=1:   ambient at 40%, directional at full     → deep shadows
    //  - s=2:   ambient near zero, directional at 1.5×  → extreme contrast
    const ambientScale = Math.max(0, 1.0 - 0.6 * s);
    const directionalScale = 0.5 + 0.5 * s;

    this.ambientLight.intensity = base.ambient * ambientScale * L;
    this.cameraLight.intensity = base.directional * directionalScale * L;
    this.hemisphereLight.intensity = base.hemisphere * L;

    // Darken hemisphere ground color with shadow level for stronger underside shading
    // Lerp from 0xcccccc (204) at s=0 to near black at s=2 (clamped to 0)
    const groundGray = Math.round(Math.max(0, 204 - 102 * s));
    this.hemisphereLight.groundColor.setRGB(groundGray / 255, groundGray / 255, groundGray / 255);
  }

  setupHelpers() {
    // Grid and axes removed - no longer displayed
  }

  animate() {
    requestAnimationFrame(() => this.animate());

    const now = performance.now();
    const delta = (now - this.lastFrameTime) / 1000;
    this.lastFrameTime = now;

    const startTime = now;

    // Update controls
    this.controls.update();

    if (this.animationCallbacks.size > 0) {
      this.animationCallbacks.forEach(callback => {
        try {
          callback(delta);
        } catch (err) {
          console.error('Animation callback error:', err);
        }
      });
    }

    // Update contour label positions to follow camera
    if (window.viewer && window.viewer.filters && typeof window.viewer.filters.updateContourLabelPositions === 'function') {
      window.viewer.filters.updateContourLabelPositions(window.viewer);
    }

    // Update Volume LIC camera for ray marching
    if (window.viewer && window.viewer.filters && typeof window.viewer.filters.updateVolumeLICCamera === 'function') {
      window.viewer.filters.updateVolumeLICCamera();
    }

    // Render scene
    this.renderer.render(this.scene, this.camera);

    // Update stats
    this.stats.renderTime = performance.now() - startTime;
    this.stats.fps = Math.round(1000 / this.stats.renderTime);
  }

  addAnimationCallback(callback) {
    if (typeof callback === 'function') {
      this.animationCallbacks.add(callback);
      return callback;
    }
    return null;
  }

  removeAnimationCallback(callback) {
    if (callback && this.animationCallbacks.has(callback)) {
      this.animationCallbacks.delete(callback);
    }
  }

  handleResize() {
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();

    this.renderer.setSize(width, height);
  }

  // Add object to scene
  addObject(id, object, metadata = {}) {
    if (this.objects.has(id)) {
      this.removeObject(id);
    }

    this.scene.add(object);
    this.objects.set(id, { object, metadata });

    // Update stats
    this.updateStats();

    return object;
  }

  // Remove object from scene
  removeObject(id) {
    const entry = this.objects.get(id);
    if (entry) {
      this.scene.remove(entry.object);
      this.disposeObject(entry.object);
      this.objects.delete(id);
      this.updateStats();
    }
  }

  // Dispose of object resources
  disposeObject(object) {
    if (object.geometry) {
      object.geometry.dispose();
    }
    if (object.material) {
      if (Array.isArray(object.material)) {
        object.material.forEach(m => m.dispose());
      } else {
        object.material.dispose();
      }
    }
    if (object.children) {
      object.children.forEach(child => this.disposeObject(child));
    }
  }

  // Clear all objects
  clearAll() {
    this.objects.forEach((entry, id) => {
      this.scene.remove(entry.object);
      this.disposeObject(entry.object);
    });
    this.objects.clear();
    this.updateStats();
  }

  // Update statistics
  updateStats() {
    this.stats.points = 0;
    this.stats.cells = 0;
    this.stats.memory = 0;

    this.objects.forEach(entry => {
      const geometry = entry.object.geometry;
      if (geometry) {
        if (geometry.attributes.position) {
          this.stats.points += geometry.attributes.position.count;
        }
        if (geometry.index) {
          this.stats.cells += geometry.index.count / 3;
        }
      }
    });

    // Estimate memory usage
    this.stats.memory = this.renderer.info.memory.geometries * 1000000; // Rough estimate
  }

  /**
   * Get the bounding box of all objects in the scene
   * @returns {Object|null} Bounds object with min/max vectors, or null if no objects
   */
  getSceneBounds() {
    const box = new THREE.Box3();
    let hasObjects = false;

    this.objects.forEach(entry => {
      if (entry.object) {
        box.expandByObject(entry.object);
        hasObjects = true;
      }
    });

    if (hasObjects && !box.isEmpty()) {
      return {
        min: box.min,
        max: box.max
      };
    }
    return null;
  }

  // Camera controls
  resetCamera() {
    // Calculate bounding box of all objects in scene
    const box = new THREE.Box3();
    let hasObjects = false;

    this.objects.forEach(entry => {
      if (entry.object) {
        box.expandByObject(entry.object);
        hasObjects = true;
      }
    });

    if (hasObjects && !box.isEmpty()) {
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const fov = this.camera.fov * (Math.PI / 180);
      let cameraZ = Math.abs(maxDim / Math.tan(fov / 2)) * 1.2;

      // Position camera at isometric-like view
      this.camera.position.set(center.x + cameraZ * 0.7, center.y + cameraZ * 0.5, center.z + cameraZ * 0.7);
      this.camera.lookAt(center);
      this.camera.up.set(0, 1, 0);
      this.controls.target.copy(center);

      // Dynamically adjust clipping planes based on scene size
      const cameraDistance = this.camera.position.distanceTo(center);
      this.camera.near = Math.max(0.01, cameraDistance / 10000);
      this.camera.far = Math.max(cameraDistance * 1000, maxDim * 1000);
      this.camera.updateProjectionMatrix();
    } else {
      // Default position if no objects
      this.camera.position.set(5, 5, 5);
      this.camera.lookAt(0, 0, 0);
      this.camera.up.set(0, 1, 0);
      this.controls.target.set(0, 0, 0);
    }

    this.controls.update();
  }

  setCameraView(view) {
    // Calculate bounding box of all objects in scene
    const box = new THREE.Box3();
    let hasObjects = false;

    this.objects.forEach(entry => {
      if (entry.object) {
        box.expandByObject(entry.object);
        hasObjects = true;
      }
    });

    // Default center and distance if no objects
    let center = new THREE.Vector3(0, 0, 0);
    let distance = 10;

    if (hasObjects && !box.isEmpty()) {
      center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const fov = this.camera.fov * (Math.PI / 180);
      distance = Math.abs(maxDim / Math.tan(fov / 2)) * 1.2; // 1.2x padding
    }

    // Position camera based on view, centered on the mesh
    switch(view) {
      case 'xy': // Looking down Z axis (top view)
        this.camera.position.set(center.x, center.y, center.z + distance);
        this.camera.up.set(0, 1, 0);
        break;
      case 'xz': // Looking down Y axis (front view)
        this.camera.position.set(center.x, center.y + distance, center.z);
        this.camera.up.set(0, 0, -1);
        break;
      case 'yz': // Looking down X axis (side view)
        this.camera.position.set(center.x + distance, center.y, center.z);
        this.camera.up.set(0, 1, 0);
        break;
    }

    // Update camera and controls to look at center
    this.camera.lookAt(center);
    this.controls.target.copy(center);

    // Dynamically adjust clipping planes
    this.camera.near = Math.max(0.01, distance / 10000);
    this.camera.far = Math.max(distance * 1000, 100000);
    this.camera.updateProjectionMatrix();

    // Reset TrackballControls internal state after changing camera/target
    if (this.controls.reset) {
      // Store current state before reset
      const currentTarget = this.controls.target.clone();
      const currentPosition = this.camera.position.clone();
      const currentUp = this.camera.up.clone();
      // Call reset and restore desired state
      this.controls.reset();
      this.camera.position.copy(currentPosition);
      this.camera.up.copy(currentUp);
      this.controls.target.copy(currentTarget);
    }
    this.controls.update();
  }

  fitCameraToObject(object) {
    const box = new THREE.Box3().setFromObject(object);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());

    const maxDim = Math.max(size.x, size.y, size.z);
    const fov = this.camera.fov * (Math.PI / 180);
    let cameraDistance = Math.abs(maxDim / Math.sin(fov / 2));
    cameraDistance *= 1.5; // Add some padding

    const direction = this.camera.position.clone().sub(center).normalize();
    this.camera.position.copy(direction.multiplyScalar(cameraDistance).add(center));

    // Dynamically adjust clipping planes based on camera distance and mesh size
    this.camera.near = Math.max(0.01, cameraDistance / 10000);
    this.camera.far = Math.max(cameraDistance * 1000, maxDim * 1000);
    this.camera.updateProjectionMatrix();

    this.controls.target.copy(center);
    this.controls.update();
  }

  /**
   * Dynamically update clipping planes based on camera distance to target
   * Called automatically during zoom/pan operations
   */
  updateClippingPlanes() {
    const cameraDistance = this.camera.position.distanceTo(this.controls.target);
    if (cameraDistance > 0) {
      this.camera.near = Math.max(0.01, cameraDistance / 10000);
      this.camera.far = cameraDistance * 1000;
      this.camera.updateProjectionMatrix();
    }
  }

  // Settings
  setBackgroundColor(color) {
    this.settings.backgroundColor = color;
    this.scene.background = new THREE.Color(color);
  }

  toggleAxes() {
    this.settings.showAxes = !this.settings.showAxes;
    this.helpers.axes.visible = this.settings.showAxes;
  }

  toggleGrid() {
    this.settings.showGrid = !this.settings.showGrid;
    this.helpers.grid.visible = this.settings.showGrid;
  }

  toggleWireframe() {
    this.settings.wireframe = !this.settings.wireframe;
    this.objects.forEach(entry => {
      // Handle both direct objects and Groups (which contain child meshes)
      const setWireframe = (obj) => {
        if (obj.material) {
          if (Array.isArray(obj.material)) {
            obj.material.forEach(m => m.wireframe = this.settings.wireframe);
          } else {
            obj.material.wireframe = this.settings.wireframe;
          }
        }
        // Recursively handle children (for Groups)
        if (obj.children) {
          obj.children.forEach(child => setWireframe(child));
        }
      };
      setWireframe(entry.object);
    });
  }

  // Screenshot
  takeScreenshot() {
    return this.renderer.domElement.toDataURL('image/png');
  }

  // Create mesh from data
  // Can be called as:
  //   createMeshFromData(vertices, indices, colors) - separate arrays
  //   createMeshFromData({vertices, triangles, lines, colors}) - object form
  createMeshFromData(verticesOrData, indices, colors = null) {
    // Handle object form: createMeshFromData({vertices, triangles, colors})
    let vertices, actualIndices, actualColors;
    if (verticesOrData && typeof verticesOrData === 'object' && !Array.isArray(verticesOrData) && !(verticesOrData instanceof Float32Array)) {
      vertices = verticesOrData.vertices;
      actualIndices = verticesOrData.triangles || verticesOrData.lines;
      actualColors = verticesOrData.colors;
    } else {
      vertices = verticesOrData;
      actualIndices = indices;
      actualColors = colors;
    }

    const geometry = new THREE.BufferGeometry();

    // Add vertices - handle both TypedArrays and nested arrays
    let positions;
    if (vertices instanceof Float32Array) {
      positions = vertices;
    } else if (Array.isArray(vertices)) {
      if (Array.isArray(vertices[0])) {
        positions = new Float32Array(vertices.flat());
      } else {
        positions = new Float32Array(vertices);
      }
    } else {
      positions = new Float32Array(vertices);
    }
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    // Add indices if provided - handle both TypedArrays and nested arrays
    if (actualIndices) {
      let indicesArray;
      if (actualIndices instanceof Uint32Array) {
        indicesArray = actualIndices;
      } else if (Array.isArray(actualIndices)) {
        if (Array.isArray(actualIndices[0])) {
          indicesArray = new Uint32Array(actualIndices.flat());
        } else {
          indicesArray = new Uint32Array(actualIndices);
        }
      } else {
        indicesArray = new Uint32Array(actualIndices);
      }
      geometry.setIndex(new THREE.BufferAttribute(indicesArray, 1));
    }

    // Add colors if provided - handle both TypedArrays and nested arrays
    if (actualColors) {
      let colorsArray;
      if (actualColors instanceof Float32Array) {
        colorsArray = actualColors;
      } else if (Array.isArray(actualColors)) {
        if (Array.isArray(actualColors[0])) {
          colorsArray = new Float32Array(actualColors.flat());
        } else {
          colorsArray = new Float32Array(actualColors);
        }
      } else {
        colorsArray = new Float32Array(actualColors);
      }
      geometry.setAttribute('color', new THREE.BufferAttribute(colorsArray, 3));
    }

    // Compute normals
    geometry.computeVertexNormals();

    // Reuse shared material for vertex-colored meshes to avoid per-part allocation
    const material = this._getSharedMaterial(!!actualColors);

    // Create mesh
    return new THREE.Mesh(geometry, material);
  }

  /**
   * Get or create a shared material for mesh creation
   * Avoids creating a new MeshPhongMaterial for every part every frame
   */
  _getSharedMaterial(hasVertexColors) {
    const key = hasVertexColors ? '_sharedVertexColorMaterial' : '_sharedPlainMaterial';
    if (!this[key]) {
      this[key] = new THREE.MeshPhongMaterial({
        vertexColors: hasVertexColors ? THREE.VertexColors : THREE.NoColors,
        side: THREE.DoubleSide,
        flatShading: false,
        emissive: 0x000000,
        specular: 0x111111,
        shininess: 30
      });
    }
    return this[key];
  }

  // Create points from data
  createPointsFromData(vertices, colors = null, size = 0.05) {
    const geometry = new THREE.BufferGeometry();

    // Add vertices
    const positions = new Float32Array(vertices.flat());
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    // Add colors if provided
    if (colors) {
      const colorsArray = new Float32Array(colors.flat());
      geometry.setAttribute('color', new THREE.BufferAttribute(colorsArray, 3));
    }

    // Create material
    const material = new THREE.PointsMaterial({
      size: size,
      vertexColors: colors ? true : false,
      sizeAttenuation: true
    });

    // Create points
    const points = new THREE.Points(geometry, material);
    return points;
  }

  // Create line from data
  createLineFromData(vertices, color = 0xffffff, options = undefined) {
    const geometry = new THREE.BufferGeometry();
    const positionArray = new Float32Array(vertices.flat());
    geometry.setAttribute('position', new THREE.BufferAttribute(positionArray, 3));

    let vertexColorsArray = null;
    if (options && options.vertexColors instanceof Float32Array && options.vertexColors.length === positionArray.length) {
      vertexColorsArray = options.vertexColors;
      geometry.setAttribute('color', new THREE.BufferAttribute(vertexColorsArray, 3));
    }

    const baseColor = color instanceof THREE.Color ? color.clone() : new THREE.Color(color);
    const materialConfig = {
      color: vertexColorsArray ? new THREE.Color(1, 1, 1) : baseColor,
      vertexColors: Boolean(vertexColorsArray)
    };

    const material = new THREE.LineBasicMaterial(materialConfig);
    const line = new THREE.Line(geometry, material);

    return line;
  }

  // Create wireframe from data (line segments)
  createWireframeFromData(vertices, lineIndices, colors = null) {
    const geometry = new THREE.BufferGeometry();

    // Add vertices
    const positions = new Float32Array(vertices.flat());
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    // Add line indices
    if (lineIndices) {
      const indices = new Uint32Array(lineIndices.flat());
      geometry.setIndex(new THREE.BufferAttribute(indices, 1));
    }

    // Add colors if provided
    if (colors) {
      const colorsArray = new Float32Array(colors.flat());
      geometry.setAttribute('color', new THREE.BufferAttribute(colorsArray, 3));
    }

    // Create material
    const material = new THREE.LineBasicMaterial({
      vertexColors: colors ? true : false,
      linewidth: 1
    });

    // Create line segments
    const wireframe = new THREE.LineSegments(geometry, material);
    return wireframe;
  }

  // Get stats
  getStats() {
    return { ...this.stats };
  }

  /**
   * Fetch geometry data from backend WITHOUT rendering it
   * Used for preloading/caching timesteps
   * Returns raw data that can be passed to renderCachedGeometry later
   */
  async fetchGeometryFromBackend(options = {}) {
    const {
      casePath,
      time,
      colorField = 'U',
      geometryType = 'surface',
      selectedParts = null,
      representation = 'surface',
      colormapSettings = null,
      dataSource = 'openfoam',
      useBinary = false  // Disabled: binary format merges parts, breaking part toggle functionality
    } = options;

    console.log('[RenderingEngine] Fetching geometry for cache:', { time, colorField, useBinary });

    try {
      const token = window.Auth?.getToken() || localStorage.getItem('coandacloud_token');
      const requestBody = {
        case_path: casePath,
        time: time,
        color_field: colorField,
        geometry_type: geometryType,
        representation: representation,
        binary: useBinary  // Request binary format from backend
      };

      if (selectedParts) {
        requestBody.selected_parts = selectedParts;
      }

      const endpoint = `/api/${dataSource}/geometry`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        // 429 = server throttling (user already has geometry request in-flight)
        // Silently skip — frontend debouncing will retry with the latest frame
        if (response.status === 429) {
          console.log('[RenderingEngine] Server busy (429), skipping frame');
          return { throttled: true, success: false };
        }
        // Try to parse error as JSON, falling back to text
        let errorMessage = `HTTP ${response.status}`;
        try {
          const text = await response.text();
          console.error('[RenderingEngine] Server error response:', text);
          try {
            const error = JSON.parse(text);
            errorMessage = error.detail || errorMessage;
          } catch {
            // Not JSON, use text directly if short enough
            if (text && text.length < 500) {
              errorMessage = `${errorMessage}: ${text}`;
            }
          }
        } catch (e) {
          console.error('[RenderingEngine] Could not read error response:', e);
        }
        throw new Error(errorMessage);
      }

      // Check if response is binary or JSON
      const contentType = response.headers.get('Content-Type') || '';
      const geometryFormat = response.headers.get('X-Geometry-Format');
      let data;

      if (geometryFormat === 'GEO1' || contentType.includes('octet-stream')) {
        // Parse binary response
        const startTime = performance.now();
        const arrayBuffer = await response.arrayBuffer();
        data = parseBinaryGeometry(arrayBuffer);
        const parseTime = performance.now() - startTime;
        console.log(`[RenderingEngine] Binary geometry parsed in ${parseTime.toFixed(1)}ms (${(arrayBuffer.byteLength / 1024 / 1024).toFixed(2)}MB)`);
      } else {
        // Parse JSON response (fallback)
        data = await response.json();
      }

      // Extract scalars - handle both unified and split-by-parts geometry
      let scalars = data.scalars;
      let scalarRange = data.scalar_range;

      if (data.split_by_parts && data.parts) {
        // Collect scalars from all parts
        // Note: Using concat instead of spread to avoid stack overflow with large arrays
        scalars = [];
        for (const part of data.parts) {
          if (part.scalars) {
            scalars = scalars.concat(part.scalars);
          }
        }
        // Use global range if available
        scalarRange = data.global_scalar_range || (data.parts[0]?.scalar_range);
      }

      // Return raw data for caching
      return {
        geometryData: data,
        scalars: scalars,
        scalarRange: scalarRange,
        boundaries: data.boundaries || {}
      };

    } catch (error) {
      console.error('[RenderingEngine] Failed to fetch geometry:', error);
      throw error;
    }
  }

  /**
   * Render previously cached geometry data
   * Takes data from fetchGeometryFromBackend and renders it
   */
  async renderCachedGeometry(cachedData, options = {}) {
    const {
      objectId = 'main-mesh',
      colormapSettings = null,
      preserveCamera = false
    } = options;

    const data = cachedData.geometryData;

    // Scalars can be at cachedData level (from viewer.js caching) or inside geometryData (from backend)
    const scalars = cachedData.scalars || data.scalars;
    const scalarRange = cachedData.scalarRange || data.scalar_range;

    // Prepare colormap settings
    let colors = null;
    let effectiveColormapSettings = null;
    if (scalars && scalarRange) {
      effectiveColormapSettings = {
        min: colormapSettings?.min ?? scalarRange.min,
        max: colormapSettings?.max ?? scalarRange.max,
        preset: colormapSettings?.preset || 'jet'
      };

      // Store scalar data for recoloring without reload
      this.lastGeometryScalars = scalars;
      this.lastGeometryScalarRange = scalarRange;
    }

    // Create the mesh using existing logic
    let object3D;

    if (data.split_by_parts && data.parts) {
      // For split parts, each part computes its own colors — no need to compute global colors here
      object3D = this.createSplitPartsMesh(data, null, effectiveColormapSettings || colormapSettings, objectId);
    } else if (data.vertices) {
      // Only compute colors for non-split geometry
      if (effectiveColormapSettings && scalars) {
        colors = this.scalarsToColors(scalars, effectiveColormapSettings);
      }
      // createMeshFromData handles both nested arrays and TypedArrays
      object3D = this.createMeshFromData({
        vertices: data.vertices,
        triangles: data.triangles,
        lines: data.lines,
        colors: colors
      });
      object3D.name = objectId;
    }

    if (!object3D) {
      throw new Error('Failed to create mesh from cached data');
    }

    // Remove existing object with same ID
    this.removeObject(objectId);

    // Store camera state before adding new object
    let savedCamera = null;
    if (preserveCamera && this.camera) {
      savedCamera = {
        position: this.camera.position.clone(),
        target: this.controls ? this.controls.target.clone() : null
      };
    }

    // Add to scene
    this.scene.add(object3D);
    this.objects.set(objectId, { object: object3D, metadata: { type: 'geometry' } });

    // Restore camera or center on new geometry
    if (savedCamera) {
      this.camera.position.copy(savedCamera.position);
      if (savedCamera.target && this.controls) {
        this.controls.target.copy(savedCamera.target);
        this.controls.update();
      }
    } else if (!preserveCamera) {
      // Center camera on new geometry for initial load
      this.centerCameraOnObject(object3D);
    }

    return {
      vertices: data.num_vertices || 0,
      triangles: data.num_triangles || 0,
      scalarField: data.scalar_field,
      boundaries: data.boundaries || cachedData.boundaries,
      available_vector_fields: data.available_vector_fields || cachedData.available_vector_fields
    };
  }

  /**
   * Helper to create mesh from split parts data
   * Extracted from loadGeometryFromBackend for reuse
   */
  createSplitPartsMesh(data, colors, colormapSettings, objectId) {
    const rootGroup = new THREE.Group();
    rootGroup.name = objectId;

    const globalRange = data.global_scalar_range || (data.parts[0]?.scalar_range);

    const getOrCreateGroup = (path) => {
      if (!path || path.length === 0) return rootGroup;
      let current = rootGroup;
      for (const segment of path) {
        let child = current.children.find(c => c.name === segment);
        if (!child) {
          child = new THREE.Group();
          child.name = segment;
          current.add(child);
        }
        current = child;
      }
      return current;
    };

    for (const part of data.parts) {
      const partName = part.part_name || part.name;
      const groupPath = part.group_path || [];

      const partColors = this.scalarsToColors(part.scalars, {
        min: colormapSettings?.min ?? globalRange?.min ?? 0,
        max: colormapSettings?.max ?? globalRange?.max ?? 1,
        preset: colormapSettings?.preset || 'jet'
      });

      // Call createMeshFromData with correct signature (vertices, indices, colors)
      const partMesh = this.createMeshFromData(part.vertices, part.triangles, partColors);
      partMesh.name = partName;
      partMesh.userData.partName = partName;  // Also store in userData for visibility checks

      const parentGroup = getOrCreateGroup(groupPath);
      parentGroup.add(partMesh);
    }

    return rootGroup;
  }

  /**
   * Load geometry from backend (streamline-style rendering)
   * Backend processes full case with VTK, sends geometry arrays
   * Frontend creates THREE.js mesh and renders with WebGL
   */
  async loadGeometryFromBackend(options = {}) {
    const {
      casePath,
      time,
      colorField = 'U',
      geometryType = 'surface',
      selectedParts = null,
      representation = 'surface',
      sliceOrigin = null,
      sliceNormal = null,
      isoField = null,
      isoValue = null,
      objectId = 'main-mesh',
      colormapSettings = null,
      preserveCamera = false,
      dataSource = 'openfoam',  // 'openfoam', 'ensight', or 'fluent'
      useBinary = true  // Use binary format by default for better performance
    } = options;

    console.log('[RenderingEngine] Loading geometry from backend:', options);

    try {
      const token = window.Auth?.getToken() || localStorage.getItem('coandacloud_token');
      const requestBody = {
        case_path: casePath,
        time: time,
        color_field: colorField,
        geometry_type: geometryType,
        representation: representation,
        binary: useBinary  // Request binary format from backend
      };

      if (selectedParts) {
        requestBody.selected_parts = selectedParts;
      }

      if (sliceOrigin && sliceNormal) {
        requestBody.slice_origin = sliceOrigin;
        requestBody.slice_normal = sliceNormal;
      }

      if (isoField && isoValue !== null) {
        requestBody.iso_field = isoField;
        requestBody.iso_value = isoValue;
      }

      // Dynamically select endpoint based on data source
      const endpoint = `/api/${dataSource}/geometry`;
      console.log('[RenderingEngine] Using endpoint:', endpoint, 'binary:', useBinary);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        // 429 = server throttling (user already has geometry request in-flight)
        // Silently skip — frontend debouncing will retry with the latest frame
        if (response.status === 429) {
          console.log('[RenderingEngine] Server busy (429), skipping frame');
          return { throttled: true, success: false };
        }
        // Try to parse error as JSON, falling back to text
        let errorMessage = `HTTP ${response.status}`;
        try {
          const text = await response.text();
          console.error('[RenderingEngine] Server error response:', text);
          try {
            const error = JSON.parse(text);
            errorMessage = error.detail || errorMessage;
          } catch {
            // Not JSON, use text directly if short enough
            if (text && text.length < 500) {
              errorMessage = `${errorMessage}: ${text}`;
            }
          }
        } catch (e) {
          console.error('[RenderingEngine] Could not read error response:', e);
        }
        throw new Error(errorMessage);
      }

      // Check if response is binary or JSON
      const contentType = response.headers.get('Content-Type') || '';
      const geometryFormat = response.headers.get('X-Geometry-Format');
      let data;

      if (geometryFormat === 'GEO1' || contentType.includes('octet-stream')) {
        // Parse binary response
        const startTime = performance.now();
        const arrayBuffer = await response.arrayBuffer();
        data = parseBinaryGeometry(arrayBuffer);
        const parseTime = performance.now() - startTime;
        console.log(`[RenderingEngine] Binary geometry parsed in ${parseTime.toFixed(1)}ms (${(arrayBuffer.byteLength / 1024 / 1024).toFixed(2)}MB)`);
      } else {
        // Parse JSON response (fallback)
        data = await response.json();
      }
      console.log('[RenderingEngine] Received geometry:', {
        vertices: data.num_vertices,
        representation: data.representation,
        triangles: data.num_triangles,
        lines: data.num_lines,
        points: data.num_points,
        scalarRange: data.scalar_range,
        availableParts: data.available_parts
      });

      // Convert scalar values to colors
      let colors = null;
      if (data.scalars && data.scalar_range) {
        // Use provided colormap settings or default to backend's scalar range
        const effectiveColormapSettings = {
          min: colormapSettings?.min ?? data.scalar_range.min,
          max: colormapSettings?.max ?? data.scalar_range.max,
          preset: colormapSettings?.preset || 'jet'
        };

        colors = this.scalarsToColors(data.scalars, effectiveColormapSettings);

        // Store scalar data for recoloring without reload
        this.lastGeometryScalars = data.scalars;
        this.lastGeometryScalarRange = data.scalar_range;
      } else {
        console.warn('[RenderingEngine] No scalar data or range provided');
      }

      // Create THREE.js object based on representation type and whether geometry is split by parts
      let object3D;

      if (data.split_by_parts && data.parts) {

        const rootGroup = new THREE.Group();
        rootGroup.name = objectId;

        // Use global scalar range for consistent coloring across all parts
        const globalRange = data.global_scalar_range || (data.parts[0]?.scalar_range);
        // Store global scalar range for split geometry (used by viewer for colormap UI)
        if (globalRange) {
          this.lastGeometryScalarRange = globalRange;
        }

        // Collect all scalars from parts for recoloring support
        // Use Array.prototype.push.apply or concat to avoid stack overflow with large arrays
        const allScalars = [];
        for (const part of data.parts) {
          if (part.scalars && part.scalars.length > 0) {
            // Use concat to avoid stack overflow with spread operator on large arrays
            for (let i = 0; i < part.scalars.length; i++) {
              allScalars.push(part.scalars[i]);
            }
          }
        }
        if (allScalars.length > 0) {
          this.lastGeometryScalars = allScalars;
        }

        // Helper to get or create nested groups based on path
        const getOrCreateGroup = (path) => {
          if (!path || path.length === 0) return rootGroup;

          let currentGroup = rootGroup;
          let currentPath = '';

          for (const segment of path) {
            currentPath = currentPath ? `${currentPath}/${segment}` : segment;

            // Check if this group already exists
            let childGroup = currentGroup.children.find(
              child => child instanceof THREE.Group && child.name === segment
            );

            if (!childGroup) {
              // Create new group
              childGroup = new THREE.Group();
              childGroup.name = segment;
              childGroup.userData.groupPath = currentPath;
              currentGroup.add(childGroup);
            }

            currentGroup = childGroup;
          }

          return currentGroup;
        };

        for (const part of data.parts) {
          const partName = part.part_name;
          const fullName = part.full_name || partName; // Use full hierarchical name if available

          // Convert scalars to colors for this part using GLOBAL range for consistency
          let partColors = null;
          if (part.scalars && globalRange && colormapSettings) {
            const partColormapSettings = {
              min: colormapSettings?.min ?? globalRange.min,
              max: colormapSettings?.max ?? globalRange.max,
              preset: colormapSettings?.preset || 'jet'
            };
            partColors = this.scalarsToColors(part.scalars, partColormapSettings);
          }

          // Create mesh for this part
          let partMesh;
          if (data.representation === 'surface') {
            partMesh = this.createMeshFromData(part.vertices, part.triangles, partColors);
          } else if (data.representation === 'wireframe') {
            partMesh = this.createWireframeFromData(part.vertices, part.lines, partColors);
          } else if (data.representation === 'points') {
            partMesh = this.createPointsFromData(part.vertices, partColors);
          }

          if (partMesh) {
            partMesh.name = partName;
            partMesh.userData.partName = partName;
            partMesh.userData.fullName = fullName;

            // Parse hierarchy from full name (e.g., "boundary/walls" -> ["boundary"])
            const pathSegments = fullName.split('/');
            const groupPath = pathSegments.slice(0, -1); // All but the last segment
            const parentGroup = getOrCreateGroup(groupPath);

            parentGroup.add(partMesh);
          }
        }

        object3D = rootGroup;

      } else {
        // Single unified mesh
        if (data.representation === 'surface') {
          object3D = this.createMeshFromData(data.vertices, data.triangles, colors);
        } else if (data.representation === 'wireframe') {
          // Create line segments for wireframe
          object3D = this.createWireframeFromData(data.vertices, data.lines, colors);
        } else if (data.representation === 'points') {
          object3D = this.createPointsFromData(data.vertices, colors);
        } else {
          throw new Error(`Unknown representation type: ${data.representation}`);
        }

        object3D.name = objectId;
      }

      // Remove old object if exists
      this.removeObject(objectId);

      // Add to scene
      this.addObject(objectId, object3D);

      // Update stats
      this.stats.points = data.num_vertices;
      this.stats.cells = data.num_triangles || data.num_lines || data.num_points || 0;

      // Center camera on object (unless preserving camera position)
      if (!preserveCamera) {
        this.centerCameraOnObject(object3D);
      }

      // Store raw backend data for caching (used by viewer.cacheGeometry)
      this.lastGeometryRawData = data;

      return {
        success: true,
        vertices: data.num_vertices,
        representation: data.representation,
        scalarField: data.scalar_field,
        availableParts: data.available_parts,
        selectedParts: data.selected_parts,
        boundaries: data.boundaries,
        available_vector_fields: data.available_vector_fields,
        field_metadata: data.field_metadata  // Component counts from actual data
      };

    } catch (error) {
      console.error('[RenderingEngine] Error loading geometry:', error);
      throw error;
    }
  }

  /**
   * Recolor existing geometry with new colormap settings
   * Does not reload from backend - just updates colors
   * Only works with backend/geometry rendering mode
   * @param {Object} colormapSettings - {min, max, preset}
   * @param {string} objectId - ID of the geometry object
   */
  recolorGeometry(colormapSettings, objectId = 'main-mesh') {
    if (!this.lastGeometryScalars || !this.lastGeometryScalarRange) {
      return false;
    }

    try {
      const colors = this.scalarsToColors(this.lastGeometryScalars, colormapSettings);

      const entry = this.objects.get(objectId);
      if (!entry) {
        return false;
      }

      const object3D = entry.object;
      const isGroup = object3D instanceof THREE.Group;

      // Handle Group (split geometry with multiple parts)
      if (isGroup) {
        const meshes = [];
        const collectMeshes = (obj) => {
          if (obj instanceof THREE.Mesh && obj.geometry && obj.geometry.attributes.color) {
            meshes.push(obj);
          }
          if (obj.children) {
            for (const child of obj.children) {
              collectMeshes(child);
            }
          }
        };
        collectMeshes(object3D);

        // colors is now a flat Float32Array [r,g,b,r,g,b,...] in 0-1 range
        // Copy directly into each mesh's color attribute
        let colorOffset = 0;
        for (const mesh of meshes) {
          const colorAttribute = mesh.geometry.attributes.color;
          const vertexCount = colorAttribute.count;
          const floatsNeeded = vertexCount * 3;

          colorAttribute.array.set(
            colors.subarray(colorOffset * 3, colorOffset * 3 + floatsNeeded)
          );
          colorAttribute.needsUpdate = true;

          colorOffset += vertexCount;
        }

        return true;
      }

      // Handle single Mesh
      if (object3D.geometry && object3D.geometry.attributes.color) {
        const colorAttribute = object3D.geometry.attributes.color;

        // colors is a flat Float32Array — copy directly
        const copyLen = Math.min(colors.length, colorAttribute.array.length);
        colorAttribute.array.set(colors.subarray(0, copyLen));
        colorAttribute.needsUpdate = true;

        return true;
      } else {
        console.warn('[recolorGeometry] Object has no color attribute');
        return false;
      }

    } catch (error) {
      console.error('[recolorGeometry] Error:', error);
      return false;
    }
  }

  /**
   * Convert scalar values to RGB colors using a colormap
   * @param {Array} scalars - Array of scalar values
   * @param {Object} colormapSettings - Colormap settings {min, max, preset}
   * @returns {Array} Array of RGB colors in 0-1 range
   */
  /**
   * Convert scalar values to RGB colors using a colormap
   * Returns a flat Float32Array [r,g,b,r,g,b,...] with values in 0-1 range
   */
  scalarsToColors(scalars, colormapSettings) {
    const { min, max, preset = 'jet' } = colormapSettings;
    return Colormap.mapDataToColorsFloat32(scalars, min, max, preset);
  }

  /**
   * Jet colormap (blue -> cyan -> green -> yellow -> red)
   */
  jetColormap(value) {
    // Clamp value to 0-1
    value = Math.max(0, Math.min(1, value));

    let r, g, b;

    if (value < 0.125) {
      r = 0;
      g = 0;
      b = 0.5 + value * 4;
    } else if (value < 0.375) {
      r = 0;
      g = (value - 0.125) * 4;
      b = 1;
    } else if (value < 0.625) {
      r = (value - 0.375) * 4;
      g = 1;
      b = 1 - (value - 0.375) * 4;
    } else if (value < 0.875) {
      r = 1;
      g = 1 - (value - 0.625) * 4;
      b = 0;
    } else {
      r = 1 - (value - 0.875) * 4;
      g = 0;
      b = 0;
    }

    return [r, g, b];
  }

  /**
   * Center camera on a specific object
   */
  centerCameraOnObject(object) {
    const box = new THREE.Box3().setFromObject(object);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const fov = this.camera.fov * (Math.PI / 180);
    let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));
    cameraZ *= 2; // Add some padding

    this.camera.position.set(center.x + cameraZ, center.y + cameraZ, center.z + cameraZ);
    this.camera.lookAt(center);

    // Dynamically adjust clipping planes based on camera distance and mesh size
    const cameraDistance = this.camera.position.distanceTo(center);
    this.camera.near = Math.max(0.01, cameraDistance / 10000);
    this.camera.far = Math.max(cameraDistance * 1000, maxDim * 1000);
    this.camera.updateProjectionMatrix();

    this.controls.target.copy(center);
    this.controls.update();
  }
}

// Export
window.RenderingEngine = RenderingEngine;
